import { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import type { LevelOpenFullDoors } from "building-types/doors/levelOpenFullDoors";
import type { LevelOpeningPlacementTable } from "building-types/openings/levelOpeningPlacementTable";
import type { DoorPlacementDeprecated, DoorPlacementNoExpr } from "building-types/openings/opening";
import type { OpeningPlacementContext } from "building-types/openings/openingPlacementContext";
import type {
  ManualOpeningPlacement,
  OpeningPlacement,
  RoomTypeOpeningPlacement,
} from "building-types/openings/openingPlacementTypes";
import { OpeningPlacementFilter } from "building-types/openings/openingTypesEnum";
import type { Room } from "building-types/rooms/roomTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import type {
  OpeningPlacementFuncOptionsLite,
  OpeningPlacementFuncOptionsLiteRoomStartDefined,
} from "../elements/openings/openingPlacementFuncOptions";
import {
  filterDifferentRoomBlocks,
  locateHalfEdgeAtWallStripParamForDeprecatedOpenings,
} from "../elements/openings/openingUtils";
import { roomTypeOpenFullAdd } from "../elements/rooms/roomTypeOpenAddFull";

export function resolveManualOpeningPlacement(
  openingPlacements: LevelOpeningPlacementTable,
  levelId: string,
  opts: OpeningPlacementFuncOptionsLite,
  selectedPlacement: ManualOpeningPlacement,
  selectedPlacementOptions: OpeningPlacementFuncOptionsLite,
  openFullDoors: LevelOpenFullDoors,
) {
  addOpeningPlacementContextToLevel(openingPlacements, levelId, opts, selectedPlacement);

  if (selectedPlacement.openings) {
    for (const index in selectedPlacement.openings) {
      const opening = selectedPlacement.openings[index];
      if (!opening) {
        continue;
      }

      // extract full openings out to eagerly use in wall generation
      if (
        (opening.type === "DOOR" || opening.type === "DOOR_V2") &&
        opening.doorType === BuiltinDoorId.OPEN_FULL &&
        selectedPlacementOptions.roomStart
      ) {
        manualOpenFullAdd(selectedPlacementOptions, opening, openFullDoors, levelId);
      }
    }
  }
}

export function resolveRoomTypeOpeningPlacement(
  selectedPlacement: RoomTypeOpeningPlacement,
  roomStart: Room | undefined,
  roomEnd: Room | undefined,
  openingPlacements: LevelOpeningPlacementTable,
  levelId: string,
  selectedPlacementOptions: OpeningPlacementFuncOptionsLite,
  openFullDoors: LevelOpenFullDoors,
) {
  if (selectedPlacement.filter) {
    let skip = false;
    switch (selectedPlacement.filter) {
      case OpeningPlacementFilter.DIFFERENT_BLOCKS: {
        if (!filterDifferentRoomBlocks(roomStart, roomEnd)) {
          skip = true;
        }
        break;
      }
    }
    if (skip) {
      return;
    }
  }

  addOpeningPlacementContextToLevel(openingPlacements, levelId, selectedPlacementOptions, selectedPlacement);

  const options = selectedPlacement.options;
  // extract full openings out to eagerly use in wall generation
  if (
    options.funcType === "DOOR" &&
    options.doorType === BuiltinDoorId.OPEN_FULL &&
    isOpeningPlacementWithRoomStartDefined(selectedPlacementOptions)
  ) {
    roomTypeOpenFullAdd(selectedPlacementOptions, openFullDoors, levelId);
  }
}

export function determineWinningHalfEdge(
  openingCandidate: OpeningPlacement | undefined,
  adjOpeningCandidate: OpeningPlacement | undefined,
) {
  // if halfEdgeWins is `false` the opposite half edge wins
  let halfEdgeWins: boolean | undefined = undefined;
  if (openingCandidate && !adjOpeningCandidate) {
    halfEdgeWins = true;
  } else if (!openingCandidate && adjOpeningCandidate) {
    halfEdgeWins = false;
  } else if (openingCandidate && adjOpeningCandidate) {
    if (openingCandidate.type !== adjOpeningCandidate.type) {
      // if the tables do not have the same precedence take the manual one
      halfEdgeWins = openingCandidate.type === "MANUAL";
    } else {
      // otherwise, dive down to placement precedence and take the first if both are equal

      // this preserves existing behavior as part of addressing strict-nulls but is admittedly funky
      if (openingCandidate?.precedence === undefined || adjOpeningCandidate?.precedence === undefined) {
        return false;
      }

      halfEdgeWins = openingCandidate?.precedence >= adjOpeningCandidate?.precedence;
    }
  }
  return halfEdgeWins;
}

function makeOpeningPlacementContext(
  placement: OpeningPlacement,
  placementOptions: OpeningPlacementFuncOptionsLite,
): OpeningPlacementContext {
  return {
    halfEdgeIndex: placementOptions.halfEdgeIndex,
    roomStartId: placementOptions.roomStart?.id,
    roomEndId: placementOptions.roomEnd?.id,
    openingPlacement: placement,
    wallIndex: placementOptions.wallIndex,
    streetOrientation: placementOptions.streetOrientation,
  };
}

function addOpeningPlacementContextToLevel(
  openingPlacements: LevelOpeningPlacementTable,
  levelId: string,
  selectedPlacementOptions: OpeningPlacementFuncOptionsLite,
  selectedPlacement: OpeningPlacement,
) {
  const placement = makeOpeningPlacementContext(selectedPlacement, selectedPlacementOptions);
  const levelOpeningPlacements = openingPlacements[levelId] || [];
  levelOpeningPlacements.push(placement);
  openingPlacements[levelId] = levelOpeningPlacements;
}

export function isOpeningPlacementWithRoomStartDefined(
  placement: OpeningPlacementFuncOptionsLite,
): placement is OpeningPlacementFuncOptionsLiteRoomStartDefined {
  return "roomStart" in placement;
}

function manualOpenFullAdd(
  selectedPlacementOptions: OpeningPlacementFuncOptionsLite,
  opening: DoorPlacementDeprecated | DoorPlacementNoExpr,
  openFullDoors: LevelOpenFullDoors,
  levelId: string,
) {
  let skip = false;
  let halfEdge = selectedPlacementOptions.halfEdgeIndex;
  let endRoomId = selectedPlacementOptions.roomEnd?.id;

  if ((opening.type === "DOOR" && opening.wallStripDomain) || opening.type === "DOOR_V2") {
    assertIsNotUndefined(selectedPlacementOptions.roomStart);
    const r = locateHalfEdgeAtWallStripParamForDeprecatedOpenings(
      selectedPlacementOptions.roomStart,
      selectedPlacementOptions.wallIndex,
      opening.param,
      opening.paramNormalized,
      selectedPlacementOptions.roomIndex,
    );
    if (!r) {
      // out of bounds
      skip = true;
    }
    if (r) {
      halfEdge = r.halfEdgeIndex;
      endRoomId = r.roomEnd?.id;
    }
  }

  if (!skip) {
    const levelOpenFullDoors: LevelOpenFullDoors = openFullDoors[levelId] || {};
    assertIsNotUndefined(selectedPlacementOptions.roomStart);
    const startRoomId = selectedPlacementOptions.roomStart.id;
    levelOpenFullDoors[halfEdge] = { startRoomId, endRoomId };
    openFullDoors[levelId] = levelOpenFullDoors;
  }
}
